import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import {
  SurveyContentPanelInset,
  SurveyFormPanel,
  surveyQuestionTextClass,
} from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { buildLdSurveySubmitBodyFromStorage, submitCustomerSurveyTng } from '@/api/customerSurvey';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { clearAllLocalStorage } from '@/lib/clearAllLocalStorage';
import { isLdProductNowComplete } from '@/lib/ldProductNowStorage';
import { readPrevBrandAnswerAnswers } from '@/lib/prevBrandAnswerStorage';
import {
  readSetBLdBrandOpinionAnswers,
  SET_B_LD_FUTURE_LIKELIHOOD_OPTIONS,
  writeSetBLdBrandOpinionAnswers,
  type SetBLdBrandOpinionAnswers,
} from '@/lib/setBLdBrandOpinionStorage';
import { surveyProfileRedirect, surveyQuestionnaireRedirect } from '@/lib/surveyFlow';
import { writeTngRewardSession } from '@/lib/tngRewardSession';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const textareaClass =
  'min-h-[140px] min-w-0 w-full resize-y rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] leading-tight text-slate-900 outline-none placeholder:text-slate-700 focus:border-slate-600 sm:min-h-[160px] sm:text-[16px]';

const inputClass =
  'min-w-0 w-full rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] text-slate-900 outline-none focus:border-slate-600 sm:text-[16px]';

export default function SetBLdBrandOpinion() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet, mobileLocalDigits } = useAppSelector(selectOtpDraft);

  const [answers, setAnswers] = useState<SetBLdBrandOpinionAnswers>(() => readSetBLdBrandOpinionAnswers());
  const [warning, setWarning] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    writeSetBLdBrandOpinionAnswers(answers);
  }, [answers]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!customerUuid || !otpVerified) return <Navigate to="/phone" replace />;
  const questionnaireRedirect = surveyQuestionnaireRedirect(questionnaireSet);
  if (questionnaireRedirect) return <Navigate to={questionnaireRedirect} replace />;
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return <Navigate to={profileRedirect} replace />;
  if (questionnaireSet !== 'B') return <Navigate to="/survey" replace />;
  if (!isLdProductNowComplete()) return <Navigate to="/ldproductnow" replace />;

  const smokedOtherProduct =
    readPrevBrandAnswerAnswers().smokedOtherProduct;
  if (smokedOtherProduct === 'yes') {
    return <Navigate to="/survey/prevbrand-other" replace />;
  }
  if (smokedOtherProduct !== 'no') {
    return <Navigate to="/ldproductnow" replace />;
  }

  const handleSubmit = () => {
    const brandOpinion = answers.brandOpinion.trim();
    const futureLikelihoodWhy = answers.futureLikelihoodWhy.trim();

    if (!brandOpinion) {
      setWarning(t.setBLdBrandOpinion.brandOpinionWarning);
      return;
    }
    if (!answers.futureLikelihood) {
      setWarning(t.setBLdBrandOpinion.likelihoodWarning);
      return;
    }
    if (!futureLikelihoodWhy) {
      setWarning(t.setBLdBrandOpinion.likelihoodWhyWarning);
      return;
    }
    if (!customerUuid) {
      setWarning(t.setBLdBrandOpinion.sessionExpiredWarning);
      return;
    }

    const saved: SetBLdBrandOpinionAnswers = {
      brandOpinion,
      futureLikelihood: answers.futureLikelihood,
      futureLikelihoodWhy,
    };
    writeSetBLdBrandOpinionAnswers(saved);
    setAnswers(saved);

    const body = buildLdSurveySubmitBodyFromStorage(mobileLocalDigits);
    if (!body) {
      setWarning(t.setBLdBrandOpinion.incompleteWarning);
      return;
    }

    setSubmitting(true);
    setWarning(null);
    void submitCustomerSurveyTng(customerUuid, body)
      .then((result) => {
        writeTngRewardSession({
          pin: result.pin,
          expirydate: result.expirydate,
          value: result.value,
        });
        clearAllLocalStorage();
        navigate('/tng', { replace: true });
      })
      .catch((err: unknown) => {
        setWarning(err instanceof Error ? err.message : t.setBLdBrandOpinion.submitError);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col pb-4">
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[9dvh]">
          <SurveyContentPanelInset className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-3 pb-4">
              <SurveyFormPanel className="flex flex-col gap-2">
                <p className={surveyQuestionTextClass}>{t.setBLdBrandOpinion.brandOpinionQuestion}</p>
                <textarea
                  id="set-b-ld-brand-opinion"
                  value={answers.brandOpinion}
                  onChange={(e) => {
                    setAnswers((prev) => ({ ...prev, brandOpinion: e.target.value }));
                    setWarning(null);
                  }}
                  placeholder={t.setBLdBrandOpinion.brandOpinionPlaceholder}
                  className={textareaClass}
                />
              </SurveyFormPanel>

              <SurveyFormPanel className="flex flex-col gap-2">
                <p id="set-b-ld-future-likelihood-question" className={surveyQuestionTextClass}>
                  {t.setBLdBrandOpinion.futureLikelihoodQuestion}
                </p>
                <select
                  id="set-b-ld-future-likelihood"
                  value={answers.futureLikelihood}
                  onChange={(e) => {
                    const futureLikelihood = e.target.value as SetBLdBrandOpinionAnswers['futureLikelihood'];
                    setAnswers((prev) => ({ ...prev, futureLikelihood }));
                    setWarning(null);
                  }}
                  className={inputClass}
                  aria-labelledby="set-b-ld-future-likelihood-question"
                >
                  <option value="" disabled>
                    {t.setBLdBrandOpinion.selectLikelihood}
                  </option>
                  {SET_B_LD_FUTURE_LIKELIHOOD_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>

                <p className={surveyQuestionTextClass}>{t.setBLdBrandOpinion.whyQuestion}</p>
                <textarea
                  id="set-b-ld-future-likelihood-why"
                  value={answers.futureLikelihoodWhy}
                  onChange={(e) => {
                    setAnswers((prev) => ({ ...prev, futureLikelihoodWhy: e.target.value }));
                    setWarning(null);
                  }}
                  className={textareaClass}
                />
              </SurveyFormPanel>
            </div>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-4 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton className="w-full" disabled={submitting} onClick={handleSubmit}>
                {t.setBLdBrandOpinion.submit}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
